import { useEffect, useMemo, useRef, useState } from 'react';
import { assessMappingFamilyRequirementsGrpc } from '../../../shared/api/grpc/familyRequirementAssessmentClient';
import type { BoltConfiguration } from '../../../shared/types';
import {
  buildSelectedMappingParameterScopeRequirements,
  type MappingParameterAssessment,
  type MappingParameterScopeRequirement,
} from '../lib/mappingReadinessFamilyExclusions';
import { buildMappingFamilyRequirementAssessmentInputs } from '../lib/mappingReadinessSnapshotRequest';
import {
  getSettingsDtoAdditionalCategoriesConfiguration,
  getSettingsDtoBoltConfigurationInput,
  getSettingsDtoConnectorConfiguration,
  getSettingsDtoMenuConfiguration,
} from '../lib/settingsDtoFamilyDestination';
import { buildProfileKey } from '../lib/profile';
import { warnMappingFlow } from '../lib/mappingTrace';
import { collectMappedParameterReferencesFromSettingsDto } from '../lib/mappingProjectReadiness';
import { toMappingParameterAssessments } from './useMappingFamilyRequirementBackendAssessment';
import type { ProfileInspectionState } from './useProfileSettingsInspection';

interface FamilyDestinationCandidate {
  profileKey: string;
  requirements: MappingParameterScopeRequirement[];
}

export interface ProfileFamilyDestinationSatisfaction {
  isReady: boolean;
  error: string | null;
  assessmentsByProfileKey: Record<string, MappingParameterAssessment[]>;
  requirementsByProfileKey: Record<string, MappingParameterScopeRequirement[]>;
}

function buildCandidate(
  candidate: ProfileInspectionState['candidates'][number]
): FamilyDestinationCandidate | null {
  if (!candidate.dto) {
    return null;
  }

  const menuConfig = getSettingsDtoMenuConfiguration(candidate.dto);
  const connectorConfig = getSettingsDtoConnectorConfiguration(candidate.dto);
  const boltConfig = getSettingsDtoBoltConfigurationInput(candidate.dto);
  const additionalCategoriesConfig = getSettingsDtoAdditionalCategoriesConfiguration(candidate.dto);
  if (!menuConfig || !connectorConfig || !boltConfig || !additionalCategoriesConfig) {
    return null;
  }

  const requirements = buildSelectedMappingParameterScopeRequirements(
    {
      menuConfig,
      connectorConfig,
      // Both shapes carry the same persisted fields; the builder normalizes them
      // through toBoltConfigurationInput before constructing requirements.
      boltConfig: boltConfig as unknown as BoltConfiguration,
      additionalCategoriesConfig,
    },
    collectMappedParameterReferencesFromSettingsDto(candidate.dto)
  );
  return requirements.length > 0
    ? { profileKey: buildProfileKey(candidate.profile), requirements }
    : null;
}

async function assessCandidateFamilyRequirements(
  candidate: FamilyDestinationCandidate
): Promise<[string, MappingParameterAssessment[]]> {
  // Project-scope requirements never cross the wire: the scoring path assesses
  // them against the project catalogs, so only the family-scope verdicts are
  // collected here.
  const familyRequirements = candidate.requirements.filter(
    (requirement) => requirement.target.kind !== 'projectCategories'
  );
  if (familyRequirements.length === 0) {
    return [candidate.profileKey, []];
  }

  const result = await assessMappingFamilyRequirementsGrpc(
    buildMappingFamilyRequirementAssessmentInputs(familyRequirements)
  );
  if (!result.success) {
    throw new Error(result.error || 'Mapping family requirement assessment failed');
  }
  return [
    candidate.profileKey,
    toMappingParameterAssessments(result.assessments ?? [], familyRequirements),
  ];
}

export function useProfileFamilyDestinationSatisfaction(input: {
  enabled: boolean;
  hostRuntimeAvailable: boolean;
  normalizedProjectKey: string | null;
  modelVersion: number | null;
  profileInspection: ProfileInspectionState;
  profileSignature: string;
}): ProfileFamilyDestinationSatisfaction {
  const {
    enabled,
    hostRuntimeAvailable,
    normalizedProjectKey,
    modelVersion,
    profileInspection,
    profileSignature,
  } = input;
  const candidates = useMemo(() => {
    if (profileInspection.isLoading || profileInspection.profileSignature !== profileSignature) {
      return [];
    }
    return profileInspection.candidates
      .map(buildCandidate)
      .filter((candidate): candidate is FamilyDestinationCandidate => candidate !== null);
  }, [profileInspection, profileSignature]);
  const candidateKey = candidates
    .filter((candidate) => candidate.requirements.some(
      (requirement) => requirement.target.kind !== 'projectCategories'
    ))
    .map((candidate) => `${candidate.profileKey}:${candidate.requirements.map((item) => item.key).join(',')}`)
    .sort()
    .join('|');
  const scopeKey = enabled && normalizedProjectKey && hostRuntimeAvailable && candidateKey
    ? `${normalizedProjectKey}::${modelVersion ?? 'unknown'}::${candidateKey}`
    : '';
  const [state, setState] = useState<{
    scopeKey: string;
    error: string | null;
    assessmentsByProfileKey: Record<string, MappingParameterAssessment[]>;
  }>({ scopeKey: '', error: null, assessmentsByProfileKey: {} });
  const candidatesRef = useRef(candidates);
  candidatesRef.current = candidates;

  useEffect(() => {
    if (!scopeKey) {
      setState((current) => current.scopeKey === ''
        ? current
        : { scopeKey: '', error: null, assessmentsByProfileKey: {} });
      return;
    }

    const fetchCandidates = candidatesRef.current;
    let isActive = true;
    // Profile family compliance must judge family documents, not swept element
    // rows — and the verdict depends on the parameter storage mode (family-type
    // vs instance vs project binding), which the snapshot-DTO matcher could not
    // see. The backend assessment is authoritative; a failed call must surface
    // as a blocked state, never as an "all missing" or "all satisfied" verdict.
    void Promise.all(fetchCandidates.map(assessCandidateFamilyRequirements))
      .then((entries) => {
        if (!isActive) {
          return;
        }
        const assessmentsByProfileKey = Object.fromEntries(entries);
        warnMappingFlow('project profile family satisfaction resolved', {
          projectKey: normalizedProjectKey,
          modelVersion,
          assessmentsByProfileKey,
        });
        setState({ scopeKey, error: null, assessmentsByProfileKey });
      })
      .catch((error: unknown) => {
        if (!isActive) {
          return;
        }
        const message = error instanceof Error
          ? error.message
          : 'Mapping family requirement assessment failed';
        warnMappingFlow('project profile family satisfaction failed', {
          projectKey: normalizedProjectKey,
          modelVersion,
          error: message,
        });
        setState({ scopeKey, error: message, assessmentsByProfileKey: {} });
      });

    return () => {
      isActive = false;
    };
  }, [modelVersion, normalizedProjectKey, scopeKey]);

  const matchesScope = state.scopeKey === scopeKey;
  return {
    isReady: scopeKey === '' || (matchesScope && !state.error),
    error: matchesScope ? state.error : null,
    assessmentsByProfileKey: state.assessmentsByProfileKey,
    requirementsByProfileKey: Object.fromEntries(
      candidates.map((candidate) => [candidate.profileKey, candidate.requirements])
    ),
  };
}
